import React, { useState, useEffect, useRef, useMemo } from "react";
import { Card } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, Tooltip
} from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import Map3D from './Map3D';
import PointTaskSelector from '../../../components/navigation/PointTaskSelector';
import ActiveMissionPanel from '../../../components/navigation/ActiveMissionPanel';
import Config from '../../../config';
import useActiveRobotMission from '../../../utils/useActiveRobotMission';
import {
  AUTH_TYPE_NONE,
  WebRTCCameraPlayer,
  shouldUseWebRTCReaderForStream,
  sanitizeUrl,
  isMixedContent
} from '../../../utils/webrtc';
import { useLatestRef } from '../../../utils/hooks';
import { snapshotRoutePreviewTarget } from '../../../utils/navigationTarget';

export default function MapView({ robot, apiConfig }) {
  const { keycloak } = useKeycloak();
  const [expandedCamera, setExpandedCamera] = useState(null);
  const [showCameras, setShowCameras] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [mapImage, setMapImage] = useState(null);
  const [mapName, setMapName] = useState("");
  const [mapId, setMapId] = useState("");
  const [, setRobotMaps] = useState([]);
  const [livestreamInfo, setLivestreamInfo] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [robotInfo, setRobotInfo] = useState(null);
  const [robotStatus, setRobotStatus] = useState(null);
  const [selectedWaypointName, setSelectedWaypointName] = useState('');
  const [pendingWaypoint, setPendingWaypoint] = useState(null);
  const [pendingMapPoint, setPendingMapPoint] = useState(null);
  const [pointTaskIds, setPointTaskIds] = useState([]);
  const [mapPointTarget, setMapPointTarget] = useState(null);
  const [routePreview, setRoutePreview] = useState(null);
  const [routeState, setRouteState] = useState('idle');
  const [routeError, setRouteError] = useState('');
  const routeRequestRef = useRef(0);
  const apiConfigRef = useRef(apiConfig);
  apiConfigRef.current = apiConfig;
  const [authToken, setAuthToken] = useState('');
  const socketRef = useRef(null);
  const {
    activeMission,
    loading: missionStateLoading,
    error: missionStateError,
    controlAction: missionControlAction,
    refresh: refreshActiveMission,
    controlMission,
  } = useActiveRobotMission(apiConfig, robotInfo?.Id);
  const hadActiveMissionRef = useRef(false);
  const missionStateLocked = Boolean(robotInfo?.Id) && (
    missionStateLoading || Boolean(missionStateError) || Boolean(activeMission)
  );
  const navigationLocked = missionStateLocked || routeState === 'dispatching';
  const previewTargetX = mapPointTarget?.x;
  const previewTargetY = mapPointTarget?.y;
  const previewTargetZ = mapPointTarget?.z;
  const previewTargetHeading = mapPointTarget?.angle;
  const activeMissionPath = String(activeMission?.mapId || '') === String(mapId || '')
    ? activeMission?.path || []
    : [];
  const activeMissionRouteState = activeMission?.status === 'PAUSED'
    ? 'paused'
    : activeMission?.status === 'IN_PROGRESS'
      ? 'travelling'
      : 'scheduled';

  useEffect(() => {
    if (!keycloak?.authenticated) {
      setAuthToken('');
      return undefined;
    }

    let mounted = true;
    const refreshToken = async () => {
      try {
        await keycloak.updateToken(30);
      } catch (error) {
        console.warn('Failed to refresh Keycloak token for WebRTC:', error);
      }

      if (mounted) {
        setAuthToken(keycloak.token || '');
      }
    };

    refreshToken();
    const intervalId = window.setInterval(refreshToken, 15000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, [keycloak]);

  const getRobotInfoById = (robot_info) => {
    Promise.all([
      apiConfig.fetchRobotType(robot_info.RobotTypeId),
      apiConfig.fetchRobotSite(robot_info.RobotSiteId),
      apiConfig.fetchRobotSoftwareVersion(robot_info.RobotSoftwareId),
      apiConfig.fetchRobotVersion(robot_info.RobotVersionId),
      apiConfig.fetchRobotStatus(robot_info.Id),
    ]).then(results => {
      robot_info["Type"] = results[0].RobotResponse
      robot_info["Site"] = results[1].RobotResponse.Site
      robot_info["Software"] = results[2].RobotResponse.Version
      robot_info["RobotVersion"] = results[3].RobotResponse.Version
      setRobotInfo(robot_info)
      console.log(robot_info)
      setRobotStatus(results[4].RobotResponse)
      console.log(results[4].RobotResponse)
      getRobotInit(robot_info, results[4].RobotResponse)
    }).catch(error => {
      console.error(error);
    });
  }

  const getRobotWaypoints = (map_name) => {
    Promise.all([
        apiConfig.fetchWaypoints(map_name),
    ]).then(results => {
        setWaypoints(results[0].RobotResponse)
    }).catch(error => {
        console.error(error);
    });
  }

  const getRobotInit = (robot_info, robot_status) => {
    Promise.all([
        apiConfig.fetchAllMaps(robot_info.Id),
        apiConfig.fetchLivestreamInfoByRobotId(robot_info.Id),
    ]).then(results => {
        console.log(results)
        const maps = results[0].RobotResponse || [];
        const currentMap = maps.find(
          m => String(m.Id) === String(robot_status.RobotMapId)
        );
        if (currentMap) {
          getRobotWaypoints(currentMap.Id)
          setMapImage(currentMap.Base64)
          setMapName(currentMap.MapName)
          setMapId(currentMap.Id)
        } else {
          setWaypoints([])
          setMapImage(null)
          setMapName("")
          setMapId("")
        }
        setRobotMaps(maps)
        console.log(maps)

        // Sanitize livestream URLs to remove embedded credentials
        const sanitizedLivestreams = results[1].RobotResponse.map(stream => ({
          ...stream,
          RawUrl: stream.Url || '',
          Url: sanitizeUrl(stream.Url)
        }));
        setLivestreamInfo(sanitizedLivestreams);
        console.log("Sanitized livestreams:", sanitizedLivestreams);
        // setLoading(false);
    }).catch(error => {
        console.error(error);
    });
  }

  const getRobotInfoByIdRef = useLatestRef(getRobotInfoById);

  useEffect(() => {
    if (robot) {
      getRobotInfoByIdRef.current(robot);
      console.log("Selected robot changed:", robot);
    }
  }, [robot, getRobotInfoByIdRef]);

  // WebSocket connection for real-time robot status updates
  useEffect(() => {
    if (!robotInfo) return;

    const socket = new WebSocket(Config.robot_status_ws);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log('MapView WebSocket connection established');
    };

    socket.onclose = () => {
      console.log('MapView WebSocket connection closed');
      if (socketRef.current === socket) socketRef.current = null;
    };

    socket.onerror = (error) => {
      console.error('MapView WebSocket error:', error);
    };

    // Handle incoming WebSocket messages for robot status
    socket.onmessage = (event) => {
      try {
        const obj = JSON.parse(event.data);

        if (obj.type === 'status' && obj.data) {
          // Update robot status if it matches current robot
          if (obj.data.RobotTableId === robotInfo.Id) {
            console.log('Updated robot location:', { x: obj.data.LocationX, y: obj.data.LocationY });
            setRobotStatus(obj.data);
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    return () => {
      socket.close();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [robotInfo]);

  const sendRobotCommand = (action, value = '') => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn('Robot command ignored because the status WebSocket is not connected');
      return false;
    }
    socketRef.current.send(JSON.stringify({
      action,
      value,
      robot_name: robotInfo?.RobotId,
      instruction: 'movement',
    }));
    return true;
  };

  const handleWaypointClick = (waypoint) => {
    setSelectedWaypointName(waypoint?.WaypointName || '');
    setPendingWaypoint(waypoint || null);
  };

  const handleConfirmWaypoint = () => {
    if (pendingWaypoint?.WaypointName) {
      sendRobotCommand('gotogoal', pendingWaypoint.WaypointName);
    }
    setPendingWaypoint(null);
  };

  useEffect(() => {
    if (activeMission) {
      hadActiveMissionRef.current = true;
      routeRequestRef.current += 1;
      setPendingMapPoint(null);
      setPointTaskIds([]);
      setRoutePreview(null);
      setRouteState(
        activeMission.status === 'PAUSED'
          ? 'paused'
          : activeMission.status === 'IN_PROGRESS'
            ? 'travelling'
            : 'scheduled'
      );
      setRouteError('');
      return;
    }

    if (hadActiveMissionRef.current) {
      hadActiveMissionRef.current = false;
      setMapPointTarget(null);
      setRoutePreview(null);
      setRouteState('idle');
      setRouteError('');
    }
  }, [activeMission]);

  useEffect(() => {
    if (navigationLocked) return undefined;
    if (
      !Number.isFinite(previewTargetX)
      || !Number.isFinite(previewTargetY)
      || !robotInfo?.Id
      || !mapId
    ) {
      routeRequestRef.current += 1;
      setRoutePreview(null);
      setRouteState('idle');
      setRouteError('');
      return undefined;
    }

    const requestId = routeRequestRef.current + 1;
    routeRequestRef.current = requestId;
    const controller = new AbortController();
    setRouteState('calculating');
    setRouteError('');
    const timer = window.setTimeout(() => {
      apiConfigRef.current.previewNavigationRoute(robotInfo.Id, mapId, {
        x: previewTargetX,
        y: previewTargetY,
        z: previewTargetZ,
        heading: previewTargetHeading,
      }, { signal: controller.signal }).then((response) => {
        if (routeRequestRef.current !== requestId) return;
        setRoutePreview(response.RobotResponse);
        setRouteState('ready');
      }).catch((error) => {
        if (controller.signal.aborted || error?.name === 'AbortError') return;
        if (routeRequestRef.current !== requestId) return;
        setRoutePreview(null);
        setRouteState('error');
        setRouteError(error?.response?.data?.Message || error.message || 'Unable to calculate route');
      });
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    previewTargetX, previewTargetY, previewTargetZ, previewTargetHeading,
    mapId, robotInfo?.Id, navigationLocked,
  ]);

  const handlePointTargetChange = (target) => {
    if (navigationLocked) return;
    setMapPointTarget((currentTarget) => (
      snapshotRoutePreviewTarget(target, currentTarget, robotStatus?.LocationZ)
    ));
  };

  const handlePointTargetClick = (target, clearTarget) => {
    if (!target || navigationLocked) return;
    setPointTaskIds([]);
    setPendingMapPoint({ ...target, clearTarget });
  };

  const handleCloseMapPointDialog = () => {
    if (routeState === 'dispatching') return;
    setPendingMapPoint(null);
    setPointTaskIds([]);
  };

  const handleConfirmMapPoint = () => {
    if (!pendingMapPoint || !routePreview?.routePlanId || routeState !== 'ready' || navigationLocked) return;
    setRouteState('dispatching');
    setRouteError('');
    apiConfig.dispatchNavigationRoute(routePreview.routePlanId, pointTaskIds).then(async () => {
      setPendingMapPoint(null);
      setPointTaskIds([]);
      setRouteState('scheduled');
      await refreshActiveMission();
    }).catch((error) => {
      setRouteState('error');
      setRouteError(error?.response?.data?.Message || error.message || 'Unable to dispatch route');
    });
  };

  const cameras = useMemo(() => {
    const base = livestreamInfo && livestreamInfo.length > 0
      ? livestreamInfo.slice(0, 4).map((stream, idx) => ({
          id: stream.Id,
          name: stream.CameraPosition || `Camera ${idx + 1}`,
          url: stream.RawUrl || stream.Url || '',
          auth: {
            Id: stream.Id,
            AuthType: stream.AuthType,
            AuthUrl: stream.AuthUrl,
            BasicAuthUsername: stream.BasicAuthUsername,
            BasicAuthPassword: stream.BasicAuthPassword,
            BearerClientId: stream.BearerClientId,
            HasBearerClientSecret: stream.HasBearerClientSecret,
          }
        }))
      : (robot?.cameras || [
          { id: "front", name: "Front Camera", url: '' },
          { id: "back",  name: "Back Camera",  url: '' },
          { id: "left",  name: "Left Camera",  url: '' },
          { id: "right", name: "Right Camera", url: '' },
          { id: "ptz",  name: "PTZ Camera",  url: '' }
        ]).slice(0, 4).map((c, idx) => ({
          id: c.id || c.camera_id || `cam-${idx}`,
          name: c.name || c.label || `Camera ${idx + 1}`,
          url: c.url || c.stream_url || c.thumbnail || '',
          auth: {
            Id: c.Id || c.id || c.camera_id,
            AuthType: c.AuthType || c.auth_type || AUTH_TYPE_NONE,
            AuthUrl: c.AuthUrl || c.auth_url || '',
            BasicAuthUsername: c.BasicAuthUsername || c.basic_auth_username || '',
            BasicAuthPassword: c.BasicAuthPassword || c.basic_auth_password || '',
            BearerClientId: c.BearerClientId || c.bearer_client_id || '',
            HasBearerClientSecret: c.HasBearerClientSecret || c.has_bearer_client_secret || false,
          }
        }));

    while (base.length < 4) {
      base.push({ id: `cam-${base.length}`, name: `Camera ${base.length + 1}`, url: '' });
    }
    return base;
  }, [livestreamInfo, robot?.cameras]);

  useEffect(() => {
    if (!robot) return;
    apiConfig.activateLivestream({ robot_id: robot.RobotId, action: showCameras ? 'start' : 'stop' })
      .then(response => console.log('Livestream', showCameras ? 'started' : 'stopped', response))
      .catch(err => console.error('Livestream action failed', err));
  }, [showCameras, robot, apiConfig]);

  const togglePin = () => {
    const newPinnedState = !isPinned;
    setIsPinned(newPinnedState);
    if (newPinnedState) {
      setShowCameras(true);
    }
  };

  const handleMouseEnter = () => {
    setShowCameras(true);
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      setShowCameras(false);
    }
  };

  return (
    <Box
      className="mapview-container"
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: 'background.default',
        borderRadius: 1,
        overflow: 'hidden'
      }}
    >
      {/* Hover trigger area - Top 40px of the container */}
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '40px',
          zIndex: 900,
          backgroundColor: 'transparent',
          cursor: 'pointer',
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {/* Hover Indicator Line */}
        <Box
          sx={{
            position: 'absolute',
            top: '3px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '40px',
            height: '3px',
            backgroundColor: '#2196F3',
            borderRadius: '2px',
            opacity: showCameras ? 0 : 0.4,
            transition: 'all 0.3s ease',
            boxShadow: '0 0 8px rgba(33, 150, 243, 0.5)',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '-4px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '0px',
              height: '0px',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #2196F3',
              opacity: showCameras ? 0 : 0.4,
              transition: 'all 0.3s ease'
            }
          }}
        />
      </Box>

      {/* Sliding Camera Panel - Overlay */}
      <Box
        className="camera-panel"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          maxHeight: showCameras ? '300px' : '0px',
          backgroundColor: '#1a1a1a',
          borderBottom: showCameras ? '2px solid #2196F3' : 'none',
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: showCameras ? '0 4px 12px rgba(0,0,0,0.3)' : 'none'
        }}
      >
        {/* Camera Controls Bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: '#0d0d0d',
            borderBottom: '1px solid #333',
            flexShrink: 0,
            position: 'relative',
            zIndex: 10
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>
              CAMERAS
            </Box>
            {isPinned && (
              <Box
                sx={{
                  fontSize: '10px',
                  color: '#2196F3',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <PushPinIcon sx={{ fontSize: '12px' }} /> PINNED
              </Box>
            )}
          </Box>
          <Tooltip title={isPinned ? "Unpin cameras" : "Pin cameras"}>
            <IconButton
              size="small"
              onClick={togglePin}
              sx={{
                color: isPinned ? '#2196F3' : '#999',
                '&:hover': {
                  color: '#2196F3',
                  backgroundColor: 'rgba(33, 150, 243, 0.1)'
                },
                transition: 'all 0.2s ease',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              {isPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Camera Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '8px',
            padding: '10px',
            width: '100%',
            pointerEvents: 'auto'
          }}
        >
          {cameras.map((camera) => (
            <Card
              key={camera.id}
              onClick={() => setExpandedCamera(camera)}
              sx={{
                position: 'relative',
                aspectRatio: '6 / 3',
                borderRadius: '10px',
                overflow: 'hidden',
                backgroundColor: '#000',
                border: '1px solid #444',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#2196F3',
                  boxShadow: '0 0 8px rgba(33, 150, 243, 0.5)',
                  transform: 'scale(1.02)'
                }
              }}
            >
              {camera.url && showCameras && !isMixedContent(camera.url) ? (
                shouldUseWebRTCReaderForStream(camera.url, camera.auth) ? (
                  <WebRTCCameraPlayer
                    url={camera.url}
                    title={camera.name}
                    token={authToken}
                    streamAuth={camera.auth}
                    muted
                    fetchBearerToken={apiConfig.fetchLivestreamBearerToken}
                  />
                ) : (
                  <Box
                    component="iframe"
                    src={camera.url}
                    title={camera.name}
                    sx={{ width: '100%', height: '100%', border: 'none', borderRadius: '10px' }}
                    allow="autoplay; fullscreen; picture-in-picture"
                  />
                )
              ) : camera.url && isMixedContent(camera.url) ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px' }}>
                  <Box sx={{ fontSize: '18px' }}>⚠️</Box>
                  <Box sx={{ color: '#f59e0b', fontSize: '10px', textAlign: 'center', lineHeight: 1.3 }}>
                    Mixed content blocked
                  </Box>
                  <Box
                    component="a"
                    href={camera.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: '#2196F3', fontSize: '10px', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Open stream
                  </Box>
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: '12px', textAlign: 'center', padding: '8px' }}>
                  No stream URL
                </Box>
              )}

              {/* Camera Label */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  padding: '4px 8px',
                  fontSize: '11px',
                  fontWeight: 500,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
              >
                {camera.name}
              </Box>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Main Content Area - Full size behind cameras */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          zIndex: 1
        }}
      >
        {/* 3D Map Environment */}
        {robot ? (
          <Map3D
            robot={robot}
            camerasVisible={showCameras}
            mapImage={mapImage}
            mapName={mapName}
            waypoints={waypoints}
            selectedWaypointName={selectedWaypointName}
            robotLocation={robotStatus ? {
              x: robotStatus.LocationX,
              y: robotStatus.LocationY,
              z: robotStatus.LocationZ,
              heading: robotStatus.Angle,
            } : null}
            onWaypointClick={handleWaypointClick}
            onPointTargetClick={handlePointTargetClick}
            onPointTargetChange={handlePointTargetChange}
            routePoints={activeMission ? activeMissionPath : routePreview?.geometry || routePreview?.points || []}
            routeState={activeMission ? activeMissionRouteState : routeState}
            targetLocked={navigationLocked}
            missionStatus={activeMission?.status || ''}
            missionTarget={activeMission?.missionType === 'temporary-point' ? activeMission.target : null}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: '14px'
            }}
          >
            Select a robot to view 3D map
          </Box>
        )}
        <ActiveMissionPanel
          mission={activeMission}
          error={missionStateError}
          controlAction={missionControlAction}
          onPause={() => controlMission('pause')}
          onResume={() => controlMission('resume')}
          onCancel={() => controlMission('cancel')}
          sx={{ bottom: 44 }}
        />
      </Box>

      <Dialog open={Boolean(pendingWaypoint)} onClose={() => setPendingWaypoint(null)}>
        <DialogTitle>Move robot to waypoint?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Move {robotInfo?.RobotId || robot?.RobotId} to{' '}
            <strong>{pendingWaypoint?.WaypointName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingWaypoint(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmWaypoint} autoFocus>
            Move Robot
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingMapPoint)}
        onClose={handleCloseMapPointDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Move robot to map point?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Move {robotInfo?.RobotId || robot?.RobotId} to map pixel{' '}
            <strong>
              ({Math.round(pendingMapPoint?.x || 0)}, {Math.round(pendingMapPoint?.y || 0)})
            </strong>{' '}
            with heading{' '}
            <strong>
              {Math.round(((pendingMapPoint?.angle || 0) * 180) / Math.PI)} degrees
            </strong>?
            {routeState === 'calculating' && <><br />Calculating the safest waypoint route...</>}
            {routePreview && <><br />Route uses {routePreview.points.filter((point) => point.type === 'waypoint').length} waypoint(s), approximately {Math.round(routePreview.estimatedDistance)} map pixels.</>}
            {routeError && <><br /><strong>{routeError}</strong></>}
          </DialogContentText>
          <PointTaskSelector
            apiConfig={apiConfig}
            open={Boolean(pendingMapPoint)}
            selectedTaskIds={pointTaskIds}
            onChange={setPointTaskIds}
            disabled={routeState === 'dispatching'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMapPointDialog} disabled={routeState === 'dispatching'}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmMapPoint} disabled={routeState !== 'ready'} autoFocus>
            {pointTaskIds.length ? 'Move & Run Tasks' : 'Move Robot'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expanded Camera Modal */}
      {expandedCamera && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            animation: 'fadeIn 0.3s ease-in-out'
          }}
          onClick={() => setExpandedCamera(null)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'relative',
              width: '90vw',
              height: '90vh',
              maxWidth: '1600px',
              maxHeight: '900px',
              backgroundColor: '#000',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
              animation: 'scaleIn 0.3s ease-out'
            }}
          >
            {/* Close Button */}
            <Box
              sx={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                zIndex: 2001
              }}
            >
              <IconButton
                onClick={() => setExpandedCamera(null)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Camera Image */}
            {expandedCamera.url && !isMixedContent(expandedCamera.url) ? (
              shouldUseWebRTCReaderForStream(expandedCamera.url, expandedCamera.auth) ? (
                <WebRTCCameraPlayer
                  url={expandedCamera.url}
                  title={expandedCamera.name}
                  token={authToken}
                  streamAuth={expandedCamera.auth}
                  muted={false}
                  fetchBearerToken={apiConfig.fetchLivestreamBearerToken}
                />
              ) : (
                <Box
                  component="iframe"
                  src={expandedCamera.url}
                  title={expandedCamera.name}
                  sx={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#000' }}
                  allow="autoplay; fullscreen; picture-in-picture"
                />
              )
            ) : expandedCamera.url && isMixedContent(expandedCamera.url) ? (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                <Box sx={{ fontSize: '48px' }}>⚠️</Box>
                <Box sx={{ color: '#f59e0b', fontSize: '14px', textAlign: 'center', maxWidth: '320px', lineHeight: 1.5 }}>
                  This stream is served over HTTP but the app is running over HTTPS. The browser blocks mixed content.
                </Box>
                <Box
                  component="a"
                  href={expandedCamera.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ color: '#2196F3', fontSize: '14px', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Open stream in new tab ↗
                </Box>
              </Box>
            ) : (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: '16px' }}>
                No camera feed available
              </Box>
            )}

            {/* Camera Label - Bottom */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: '#fff',
                padding: '16px',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {expandedCamera.name}
            </Box>
          </Box>
        </Box>
      )}

      {/* Keyframe animations */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </Box>
  );
}
